import React from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { Message, Icon } from 'semantic-ui-react';
import styled from 'styled-components';
import NewAssessmentDetails from '../PerformanceReviews/NewAssessmentDetails';
import LiveMeetingQABreakdown from './LiveMeetingQABreakdown';
import MeetingStatusBadge, { getStatusBannerCopy } from './MeetingStatusBadge';
import TranscriptSection from './TranscriptSection';
import { DownloadReportButton, ShareSummaryButton } from './DownloadReportSection';
import theme from '../../pagesv2/theme';
import {
  CardBody, StatusBanner, Section, SectionTitle, SectionContent,
  InfoGrid, InfoField, InfoLabel, InfoValue,
  ParticipantList, ParticipantChip, ListBlock, Placeholder,
  AlignmentSection, AlignmentHeader, AlignmentTitle, FitLevelBadge, AlignmentSummary,
  MatchCard, MatchProblem, QuoteBlock, QuoteAttribution, TagRow, SmallTag,
  SalesActionRow, AlignmentEmptyState,
  ProspectProfileGrid, ProfileColumn, ProfileGroup, ProfileGroupLabel,
  FitFooter, FitFooterRow
} from '../../pages/MyMeetingsPage.styles';

// ── Sub-renderers for the rich analysis shapes the developer's analyzer produces ──

const ItemCard = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.background};
  border-left: 3px solid ${p => p.$accent || theme.colors.primary};
  border-radius: ${theme.radii.sm};
  margin-bottom: ${theme.spacing.sm};
  &:last-child { margin-bottom: 0; }
`;

const ItemHead = styled.div`
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
  font-weight: 500;
  line-height: 1.5;
`;

const ItemSub = styled.div`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
  margin-top: 4px;
  line-height: 1.5;
  & > strong { color: ${theme.colors.text}; }
`;

const ItemTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const Tag = styled.span`
  display: inline-block;
  font-size: ${theme.typography.tiny.size};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: ${theme.radii.full};
  background: ${p => p.$bg || theme.colors.borderLight};
  color: ${p => p.$color || theme.colors.textSecondary};
  font-weight: 600;
`;

const SubColumns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};
  ${theme.media.tablet} { grid-template-columns: 1fr; }
`;

// Mirror of Assessment.positivesView / areasForImprovementView (MobX computed
// views in ui/src/models/Assessment.jsx). NewAssessmentDetails reads these
// fields directly on the assessment, but our API returns a plain JSON object
// without the model views, so we compute them here.
const splitOverviewString = (s) => {
  if (!s || typeof s !== 'string') return [];
  return s.split('- ').slice(1).map(x => x.trim()).filter(Boolean);
};

const computePositives = (a) => {
  if (Array.isArray(a.pointsDescriptions) && a.pointsDescriptions.length > 0) {
    return a.pointsDescriptions
      .filter(p => p.maxPoints ? (p.pointsAwarded / p.maxPoints) >= 0.65 : p.pointsAwarded > 0)
      .map(p => p.description);
  }
  return splitOverviewString(a.companyOverviewPositives);
};

const computeAreasForImprovement = (a) => {
  if (Array.isArray(a.pointsDescriptions) && a.pointsDescriptions.length > 0) {
    return a.pointsDescriptions
      .filter(p => p.maxPoints ? (p.pointsAwarded / p.maxPoints) < 0.65 : p.pointsAwarded === 0)
      .map(p => p.description);
  }
  return splitOverviewString(a.companyOverviewAreasForImprovement);
};

const withDerivedViews = (assessment) => ({
  ...assessment,
  positivesView: computePositives(assessment),
  areasForImprovementView: computeAreasForImprovement(assessment),
});

const renderText = (v) => {
  if (v == null) return null;
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'object') return v.description || v.text || v.title || JSON.stringify(v);
  return String(v);
};

const severityColor = (sev) => {
  const s = (sev || '').toLowerCase();
  if (s === 'high' || s === 'critical') return { bg: theme.colors.dangerLight, color: theme.colors.danger };
  if (s === 'medium') return { bg: theme.colors.warningLight, color: theme.colors.warning };
  if (s === 'low') return { bg: theme.colors.successLight, color: theme.colors.success };
  return null;
};

const priorityColor = (pri) => {
  const p = (pri || '').toLowerCase();
  if (p === 'high' || p === 'urgent') return { bg: theme.colors.dangerLight, color: theme.colors.danger };
  if (p === 'medium') return { bg: theme.colors.warningLight, color: theme.colors.warning };
  if (p === 'low') return { bg: theme.colors.borderLight, color: theme.colors.textSecondary };
  return null;
};

const sentimentColor = (sent) => {
  const s = (sent || '').toLowerCase();
  if (s === 'positive') return { bg: theme.colors.successLight, color: theme.colors.success };
  if (s === 'negative') return { bg: theme.colors.dangerLight, color: theme.colors.danger };
  if (s === 'neutral' || s === 'mixed') return { bg: theme.colors.warningLight, color: theme.colors.warning };
  return null;
};

// ── Problem Alignment renderer ────────────────────────────────────────────────

const FIT_LEVEL_STYLES = {
  strong:   { label: 'Strong fit',      bg: theme.colors.successLight, color: theme.colors.success, border: theme.colors.success },
  moderate: { label: 'Moderate fit',    bg: theme.colors.warningLight, color: theme.colors.warning, border: theme.colors.warning },
  weak:     { label: 'Weak fit',        bg: theme.colors.dangerLight,  color: theme.colors.danger,  border: theme.colors.danger },
  none:     { label: 'No matches',      bg: theme.colors.borderLight,  color: theme.colors.textSecondary, border: theme.colors.border },
};

const EVIDENCE_STYLES = {
  explicit: { bg: theme.colors.successLight, color: theme.colors.success },
  implied:  { bg: theme.colors.warningLight, color: theme.colors.warning },
  possible: { bg: theme.colors.borderLight,  color: theme.colors.textSecondary },
};

const ProblemAlignmentSection = ({ alignment }) => {
  const fitLevel = (alignment.fitLevel || 'none').toLowerCase();
  const fitStyles = FIT_LEVEL_STYLES[fitLevel] || FIT_LEVEL_STYLES.none;
  const matches = Array.isArray(alignment.matches) ? alignment.matches : [];

  return (
    <AlignmentSection>
      <AlignmentHeader>
        <AlignmentTitle>Problem Alignment</AlignmentTitle>
        <FitLevelBadge $bg={fitStyles.bg} $color={fitStyles.color} $border={fitStyles.border}>
          {fitStyles.label}
        </FitLevelBadge>
      </AlignmentHeader>

      {alignment.summary && <AlignmentSummary>{alignment.summary}</AlignmentSummary>}

      {matches.length === 0 ? (
        <AlignmentEmptyState>
          Murphy didn't hear any of your addressable problems mentioned in this conversation.
          Consider asking discovery questions about the problems you solve to surface fit signals.
        </AlignmentEmptyState>
      ) : (
        matches.map((m, i) => {
          const evidence = EVIDENCE_STYLES[(m.evidenceStrength || '').toLowerCase()];
          const offerings = Array.isArray(m.matchingOfferings) ? m.matchingOfferings : [];
          return (
            <MatchCard key={i}>
              <MatchProblem>{m.addressableProblem || 'Addressable problem'}</MatchProblem>
              {m.prospectStatement && (
                <QuoteBlock>
                  &ldquo;{m.prospectStatement}&rdquo;
                  {m.speaker && m.speaker !== 'unknown' && (
                    <QuoteAttribution>— {m.speaker}</QuoteAttribution>
                  )}
                </QuoteBlock>
              )}
              <TagRow>
                {m.evidenceStrength && (
                  <SmallTag $bg={evidence?.bg} $color={evidence?.color}>
                    {m.evidenceStrength}
                  </SmallTag>
                )}
                {offerings.map((name, oi) => (
                  <SmallTag key={oi} $bg={theme.colors.primaryGhost} $color={theme.colors.primary}>
                    {name}
                  </SmallTag>
                ))}
              </TagRow>
              {m.salesAction && (
                <SalesActionRow>
                  <strong>Next step:</strong> {m.salesAction}
                </SalesActionRow>
              )}
            </MatchCard>
          );
        })
      )}
    </AlignmentSection>
  );
};

// ── Prospect Profile renderer ─────────────────────────────────────────────────

const TARGET_FIT_STYLES = {
  excellent: { bg: theme.colors.successLight, color: theme.colors.success },
  good:      { bg: theme.colors.successLight, color: theme.colors.success },
  moderate:  { bg: theme.colors.warningLight, color: theme.colors.warning },
  poor:      { bg: theme.colors.dangerLight,  color: theme.colors.danger },
};

const ProspectProfileSection = ({ profile }) => {
  if (!profile) return null;
  const painPoints = Array.isArray(profile.painPoints) ? profile.painPoints : [];
  const buyingSignals = Array.isArray(profile.buyingSignals) ? profile.buyingSignals : [];
  const decisionCriteria = Array.isArray(profile.decisionCriteria) ? profile.decisionCriteria : [];
  const fit = (profile.targetMarketFit || '').toLowerCase();
  const fitStyles = TARGET_FIT_STYLES[fit];
  const hasContent = painPoints.length || buyingSignals.length || decisionCriteria.length || profile.fitReasoning || profile.targetMarketFit;
  if (!hasContent) return null;

  return (
    <Section>
      <SectionTitle>Prospect Profile</SectionTitle>
      <ProspectProfileGrid>
        <ProfileColumn>
          {painPoints.length > 0 && (
            <ProfileGroup>
              <ProfileGroupLabel>Pain Points</ProfileGroupLabel>
              <ListBlock>
                {painPoints.map((p, i) => <li key={i}>{renderText(p)}</li>)}
              </ListBlock>
            </ProfileGroup>
          )}
          {decisionCriteria.length > 0 && (
            <ProfileGroup>
              <ProfileGroupLabel>Decision Criteria</ProfileGroupLabel>
              <ListBlock>
                {decisionCriteria.map((c, i) => <li key={i}>{renderText(c)}</li>)}
              </ListBlock>
            </ProfileGroup>
          )}
        </ProfileColumn>
        <ProfileColumn>
          {buyingSignals.length > 0 && (
            <ProfileGroup>
              <ProfileGroupLabel>Buying Signals</ProfileGroupLabel>
              <ListBlock>
                {buyingSignals.map((s, i) => <li key={i}>{renderText(s)}</li>)}
              </ListBlock>
            </ProfileGroup>
          )}
        </ProfileColumn>
      </ProspectProfileGrid>
      {(profile.targetMarketFit || profile.fitReasoning) && (
        <FitFooter>
          {profile.targetMarketFit && (
            <FitFooterRow>
              <ProfileGroupLabel style={{ marginBottom: 0 }}>Target Market Fit</ProfileGroupLabel>
              <SmallTag $bg={fitStyles?.bg} $color={fitStyles?.color}>
                {profile.targetMarketFit}
              </SmallTag>
            </FitFooterRow>
          )}
          {profile.fitReasoning && <div>{profile.fitReasoning}</div>}
        </FitFooter>
      )}
    </Section>
  );
};

const AnalysisSections = ({ analysis, grade, sessionType, murphyReviewSlot }) => {
  const summary = analysis.summary || {};
  const summaryText = summary.overview || summary.overallSummary || summary.summary;
  const sentTag = sentimentColor(summary.overallSentiment);
  const showWhoWeArePitchBanner = grade === 'red' && sessionType === 'Live Meeting';

  return (
    <>
      {/* Summary */}
      {summaryText && (
        <Section>
          <SectionTitle>Summary</SectionTitle>
          <SectionContent>{summaryText}</SectionContent>
          {(summary.meetingType || summary.dealStage || summary.overallSentiment) && (
            <ItemTags style={{ marginTop: theme.spacing.sm }}>
              {summary.meetingType && <Tag>{summary.meetingType}</Tag>}
              {summary.dealStage && <Tag>{summary.dealStage}</Tag>}
              {summary.overallSentiment && (
                <Tag $bg={sentTag?.bg} $color={sentTag?.color}>{summary.overallSentiment}</Tag>
              )}
            </ItemTags>
          )}
        </Section>
      )}

      {/* Red-grade coaching banner — surface when Murphy graded a real meeting
          red. The core "Who We Are" message wasn't delivered; point the rep at
          the Casual Encounter pitch they practice in Training Hub. */}
      {showWhoWeArePitchBanner && (
        <Message negative icon style={{ marginBottom: '1rem' }}>
          <Icon name="bullhorn" />
          <Message.Content>
            <Message.Header>Deliver the "Who We Are" pitch to this prospect</Message.Header>
            <p>
              Red grade — the core company message wasn't fully delivered on this call.
              Sharpen the Casual Encounter pitch in Training Hub, then bring it to your next conversation with this prospect.
            </p>
            <Link to="/record-presentation/casual_encounter">Practice the Casual Encounter →</Link>
          </Message.Content>
        </Message>
      )}

      {/* Problem Alignment — top-of-report when present.
          Fall back to legacy `painPointAlignment` field so assessments
          analyzed before the rename still render. */}
      {(analysis.problemAlignment || analysis.painPointAlignment) && (
        <ProblemAlignmentSection alignment={analysis.problemAlignment || analysis.painPointAlignment} />
      )}

      {/* Murphy review (Positives / Areas For Improvement / Q&A breakdown).
          Slotted in directly after Problem Alignment so the headline coaching
          artifact reads right under the deal-fit context, before the deal
          intelligence details below. Rendered via slot so MeetingDetail can
          own the Assessment-model derivation. */}
      {murphyReviewSlot}

      {/* Key Moments */}
      {Array.isArray(analysis.keyMoments) && analysis.keyMoments.length > 0 && (
        <Section>
          <SectionTitle>Key Moments</SectionTitle>
          {analysis.keyMoments.map((m, i) => {
            if (typeof m === 'string') return <ItemCard key={i}><ItemHead>{m}</ItemHead></ItemCard>;
            const accent = (m.type || '').toLowerCase().includes('risk') ? theme.colors.danger
              : (m.type || '').toLowerCase().includes('opportunity') ? theme.colors.success
              : theme.colors.primary;
            return (
              <ItemCard key={i} $accent={accent}>
                <ItemHead>{m.description || renderText(m)}</ItemHead>
                {m.significance && <ItemSub><strong>Why it matters:</strong> {m.significance}</ItemSub>}
                {(m.type || m.timestamp) && (
                  <ItemTags>
                    {m.type && <Tag>{m.type}</Tag>}
                    {m.timestamp && <Tag>{m.timestamp}</Tag>}
                  </ItemTags>
                )}
              </ItemCard>
            );
          })}
        </Section>
      )}

      {/* Opportunities */}
      {Array.isArray(analysis.opportunities) && analysis.opportunities.length > 0 && (
        <Section>
          <SectionTitle>Opportunities</SectionTitle>
          {analysis.opportunities.map((o, i) => {
            if (typeof o === 'string') return <ItemCard key={i} $accent={theme.colors.success}><ItemHead>{o}</ItemHead></ItemCard>;
            return (
              <ItemCard key={i} $accent={theme.colors.success}>
                <ItemHead>{o.description || renderText(o)}</ItemHead>
                {o.recommendation && <ItemSub><strong>Recommendation:</strong> {o.recommendation}</ItemSub>}
                {o.companyAlignment && <ItemSub><strong>Why we fit:</strong> {o.companyAlignment}</ItemSub>}
                {o.type && <ItemTags><Tag $bg={theme.colors.successLight} $color={theme.colors.success}>{o.type}</Tag></ItemTags>}
              </ItemCard>
            );
          })}
        </Section>
      )}

      {/* Risks */}
      {Array.isArray(analysis.risks) && analysis.risks.length > 0 && (
        <Section>
          <SectionTitle>Risks</SectionTitle>
          {analysis.risks.map((r, i) => {
            if (typeof r === 'string') return <ItemCard key={i} $accent={theme.colors.danger}><ItemHead>{r}</ItemHead></ItemCard>;
            const sev = severityColor(r.severity);
            return (
              <ItemCard key={i} $accent={theme.colors.danger}>
                <ItemHead>{r.description || renderText(r)}</ItemHead>
                {r.mitigation && <ItemSub><strong>Mitigation:</strong> {r.mitigation}</ItemSub>}
                {(r.type || r.severity) && (
                  <ItemTags>
                    {r.type && <Tag>{r.type}</Tag>}
                    {r.severity && <Tag $bg={sev?.bg} $color={sev?.color}>{r.severity} severity</Tag>}
                  </ItemTags>
                )}
              </ItemCard>
            );
          })}
        </Section>
      )}

      {/* Next Steps */}
      {Array.isArray(analysis.nextSteps) && analysis.nextSteps.length > 0 && (
        <Section>
          <SectionTitle>Next Steps</SectionTitle>
          {analysis.nextSteps.map((s, i) => {
            if (typeof s === 'string') return <ItemCard key={i}><ItemHead>{s}</ItemHead></ItemCard>;
            const pri = priorityColor(s.priority);
            return (
              <ItemCard key={i} $accent={theme.colors.primary}>
                <ItemHead>{s.action || renderText(s)}</ItemHead>
                {(s.owner || s.timeline) && (
                  <ItemSub>
                    {s.owner && <><strong>Owner:</strong> {s.owner}</>}
                    {s.owner && s.timeline && ' · '}
                    {s.timeline && <><strong>When:</strong> {s.timeline}</>}
                  </ItemSub>
                )}
                {s.priority && (
                  <ItemTags>
                    <Tag $bg={pri?.bg} $color={pri?.color}>{s.priority} priority</Tag>
                  </ItemTags>
                )}
              </ItemCard>
            );
          })}
        </Section>
      )}

      {/* Prospect Profile — render existing-but-orphaned prospectProfile data */}
      {analysis.prospectProfile && (
        <ProspectProfileSection profile={analysis.prospectProfile} />
      )}
    </>
  );
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  } catch { return iso; }
};

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const MeetingDetail = observer(({
  meeting,
  myMeetingsStore,
  companyStore,
  usersStore,
  scope
}) => {
  const banner = getStatusBannerCopy(meeting.status);
  const isFailed = meeting.status === 'failed';
  const isDelivered = meeting.status === 'delivered';
  const assessment = meeting.assessment;

  const onSaveToLibrary = async (questionsToSave) => {
    if (!companyStore?.company) return;
    const existing = (companyStore.company.questionsAndAnswers || []).map(qa => ({
      question: qa.question,
      answer: qa.answer,
      related_to: qa.related_to,
      ...(qa.uploadKey ? { uploadKey: qa.uploadKey } : {})
    }));
    const newQuestions = questionsToSave.filter(q =>
      !existing.some(eq => eq.question?.trim() === q.question?.trim())
    );
    if (newQuestions.length === 0) return;
    await companyStore.company.updateQuestionsAndAnswers([...existing, ...newQuestions]);
  };

  // Managers/admins/executives can add questions from any meeting they can
  // view — including team-scope meetings, not just their own. The role gates
  // below already exclude Sales Members and any read-only role.
  const canEditLibrary = !!usersStore?.currentUser && (
    !usersStore.currentUser.isSalesMember &&
    !usersStore.currentUser.isReadOnlyExecutive &&
    !usersStore.currentUser.isReadOnlySalesManager &&
    !usersStore.currentUser.isReadOnlySeniorManager
  );

  const participants = meeting.participants && meeting.participants.length > 0
    ? meeting.participants
    : (meeting.validatedParticipants || []).map(email => ({ email, name: email, isMultiplyUser: false }));

  return (
    <CardBody>
      {/* Status banner — only for non-delivered meetings */}
      {banner && (
        <StatusBanner $variant={isFailed ? 'failed' : 'info'}>
          <strong>
            <MeetingStatusBadge status={meeting.status} />
          </strong>
          <span style={{ marginLeft: 8 }}>{banner}</span>
        </StatusBanner>
      )}

      {/* Meeting header info — info columns left, Download report button right.
          The info fields use natural width (auto, not 1fr) so they sit tight
          and leave the right edge for the action. */}
      <Section>
        <SectionTitle>Meeting Details</SectionTitle>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', flex: 1 }}>
            <InfoField>
              <InfoLabel>When</InfoLabel>
              <InfoValue>{formatDate(meeting.meetingDate) || 'TBD'}</InfoValue>
            </InfoField>
            {formatDuration(meeting.duration) && (
              <InfoField>
                <InfoLabel>Duration</InfoLabel>
                <InfoValue>{formatDuration(meeting.duration)}</InfoValue>
              </InfoField>
            )}
            {meeting.consentNotice && (
              <InfoField>
                <InfoLabel>Recording Notice</InfoLabel>
                <InfoValue style={{ fontSize: 13 }}>
                  {meeting.consentNotice.delivered === true &&
                    `Shown in the meeting${meeting.consentNotice.sentAt ? ` at ${formatDate(meeting.consentNotice.sentAt)}` : ''}`}
                  {meeting.consentNotice.delivered === false &&
                    'Not shown (Murphy never made it into the call)'}
                  {meeting.consentNotice.delivered == null && 'Configured for this meeting'}
                </InfoValue>
              </InfoField>
            )}
            {scope === 'team' && meeting.salesPersonName && (
              <InfoField>
                <InfoLabel>Sales Rep</InfoLabel>
                <InfoValue>{meeting.salesPersonName}</InfoValue>
              </InfoField>
            )}
            {meeting.assessment?.dealStage && meeting.assessment.dealStage !== 'unknown' && (
              <InfoField>
                <InfoLabel>Deal Stage</InfoLabel>
                <InfoValue style={{ textTransform: 'capitalize' }}>{meeting.assessment.dealStage}</InfoValue>
              </InfoField>
            )}
          </div>
          {isDelivered && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <ShareSummaryButton meeting={meeting} />
                <DownloadReportButton meeting={meeting} />
              </div>
              {Array.isArray(meeting.sharedEmailsSent) && meeting.sharedEmailsSent.length > 0 && (
                <div style={{ fontSize: 11, color: '#7a8590', maxWidth: 360, textAlign: 'right', lineHeight: 1.4 }}>
                  Auto-shared with: {meeting.sharedEmailsSent.map(r => r.email).join(', ')}
                </div>
              )}
            </div>
          )}
        </div>

        {participants.length > 0 ? (
          <>
            <InfoLabel style={{ marginTop: 16, display: 'block' }}>Participants</InfoLabel>
            <ParticipantList>
              {participants.map((p, i) => (
                <ParticipantChip key={i} $isMultiplyUser={p.isMultiplyUser}>
                  {p.isMultiplyUser && '★ '}{p.name || p.email}
                </ParticipantChip>
              ))}
            </ParticipantList>
          </>
        ) : (
          <Placeholder>Participants: TBD</Placeholder>
        )}
      </Section>

      {/* Defensive: delivered but missing assessment */}
      {isDelivered && !assessment && (
        <Section>
          <Placeholder>
            Analysis data is missing for this meeting. Contact support if this persists.
          </Placeholder>
        </Section>
      )}

      {/* Analysis sections — only for delivered meetings with an assessment.
          The Murphy review block (Positives / Areas For Improvement / Q&A
          breakdown) is slotted in *between* Problem Alignment and Key Moments
          inside AnalysisSections, so the headline coaching artifact sits next
          to the deal-fit context rather than at the very bottom of the
          analysis stack. Reuses NewAssessmentDetails — the same component
          practice-pitch reviews use — by feeding it a derived view object
          (positivesView/areasForImprovementView are MobX computed views on
          the Assessment model; our endpoint returns plain JSON). */}
      {isDelivered && assessment?.liveMeetingAnalysis && (
        <AnalysisSections
          analysis={assessment.liveMeetingAnalysis}
          grade={assessment.grade}
          sessionType={assessment.sessionType}
          murphyReviewSlot={
            assessment ? (
              <Section>
                <NewAssessmentDetails
                  assessments={[withDerivedViews(assessment)]}
                  canEditLibrary={canEditLibrary}
                  existingQuestions={companyStore?.company?.questionsAndAnswers || []}
                  onSaveToLibrary={canEditLibrary ? onSaveToLibrary : undefined}
                  hideQuestionBreakdown={true}
                />
              </Section>
            ) : null
          }
        />
      )}

      {/* Question Breakdown — live-meeting Q&A is transcript context, not
          training. Renders Q+A by speaker (seller-asked / prospect-asked)
          via LiveMeetingQABreakdown — a dedicated component that does NOT
          share code with the practice-pitch NewAssessmentDetails (which
          keeps its full coaching shape). The upper Murphy review block
          (Positives / Areas) is slotted in higher up via murphyReviewSlot
          and still uses NewAssessmentDetails. */}
      {isDelivered && assessment?.liveMeetingAnalysis && (
        <Section>
          <LiveMeetingQABreakdown
            assessment={assessment}
            canEditLibrary={canEditLibrary}
            existingQuestions={companyStore?.company?.questionsAndAnswers || []}
            onSaveToLibrary={canEditLibrary ? onSaveToLibrary : undefined}
          />
        </Section>
      )}

      {/* Edge case: delivered + assessment exists but liveMeetingAnalysis is
          missing (older assessment, partial analysis). Surface the Murphy
          review block on its own so the user still sees Positives / Areas. */}
      {isDelivered && assessment && !assessment.liveMeetingAnalysis && (
        <Section>
          <NewAssessmentDetails
            assessments={[withDerivedViews(assessment)]}
            canEditLibrary={canEditLibrary}
            existingQuestions={companyStore?.company?.questionsAndAnswers || []}
            onSaveToLibrary={canEditLibrary ? onSaveToLibrary : undefined}
          />
        </Section>
      )}

      {/* Transcript — collapsed by default */}
      {isDelivered && (
        <TranscriptSection
          meeting={meeting}
          myMeetingsStore={myMeetingsStore}
          companyId={companyStore?.company?.id}
        />
      )}
    </CardBody>
  );
});

export default MeetingDetail;
